import React from 'react'
import { Toaster } from 'react-hot-toast'
import { Provider } from 'react-redux'
import classNames from 'classnames'

import store from './store'
import PilotContext from './contexts/PilotContext'
import FastActionButton from './components/FastActionButton'
import FastActionChat from './components/FastActionChat'

interface Props {
  props: {
    agentKey: string
    authenticated: boolean
    context: object
    threadKey: string
  }
}

function FastActionWidget({ props: { agentKey, authenticated, context, threadKey } }: Props) {
  const [collapsed, setCollapsed] = React.useState(true)
  const [showCompletions, setShowCompletions] = React.useState(false)
  const [isClosing, setIsClosing] = React.useState(false)
  const [initialMessage, setInitialMessage] = React.useState<string | undefined>(undefined)
  const closeTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const containerClassNames = classNames({
    'pilot-faw': true,
    'expanded': !collapsed,
    'collapsed': collapsed,
  })

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const handleButtonClicked = (message?: string) => {
    clearCloseTimeout()

    if (message) {
      setInitialMessage(message)
    }
    setCollapsed(false)
    setIsClosing(false)
  }

  const handleChatClose = () => {
    clearCloseTimeout()

    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      setCollapsed(true)
      setIsClosing(false)
      setInitialMessage(undefined)
      closeTimeoutRef.current = null
    }, 400)
  }

  React.useEffect(() => {
    return () => {
      clearCloseTimeout()
    }
  }, [])

  React.useEffect(() => {
    // We don't want to connect unless the user opens the widget, then we stay connected
    if (false === collapsed && false == showCompletions) {
      setShowCompletions(true)
    }
  }, [collapsed])

  return (
    <Provider store={store}>
      <PilotContext.Provider value={{ agentKey, authenticated, context, threadKey }}>
        <Toaster position='top-center' />
        <div className={containerClassNames}>
          <FastActionButton clicked={handleButtonClicked} />
          {(showCompletions && !collapsed) || isClosing ? (
            <FastActionChat
              close={handleChatClose}
              initialMessage={initialMessage}
              isClosing={isClosing}
            />
          ) : null}
        </div>
      </PilotContext.Provider>
    </Provider>
  )
}

export default FastActionWidget
